import CalcInput from "@/components/calculators/CalcInput"
import ResultCard from "@/components/calculators/ResultCard"
import PageHero from "@/components/PageHero"
import SEOHead from "@/components/SEOHead"
import { formatPercent, formatRatio, safeDivide, toNumber } from "@/lib/format"
import { useState } from "react"

function AcosConverter() {
  const [acos, setAcos] = useState("25")
  const acosNum = toNumber(acos)

  const roasRatio = safeDivide(100, acosNum)
  const roasPercent = roasRatio * 100

  return (
    <div>
      <SEOHead
        title="Amazon ACoS to ROAS Converter"
        description="Convert Amazon Advertising Cost of Sales (ACoS) into an equivalent ROAS ratio and percentage instantly."
      />

      <PageHero
        eyebrow="Amazon ads"
        title="Amazon ACoS to ROAS Converter"
        description="Convert your Amazon Advertising Cost of Sales (ACoS) into an equivalent ROAS ratio."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 pb-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-7">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] gap-6">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-5">Input</p>
              <CalcInput
                label="Amazon Advertising Cost of Sales (ACoS)"
                suffix="%"
                value={acos}
                onChange={setAcos}
                placeholder="25"
                tooltip="ACoS = Ad Spend / Attributed Sales x 100. Found in your Amazon Advertising campaign reports."
              />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-5">Equivalent ROAS</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ResultCard label="Equivalent ROAS Ratio" value={formatRatio(roasRatio)} emphasis formula="100 / ACoS%" />
                <ResultCard label="Equivalent ROAS %" value={formatPercent(roasPercent)} formula="(100 / ACoS%) x 100" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-4">ROAS and ACoS: An Inverse Relationship</h2>
        <p className="text-[15px] text-slate-600 leading-relaxed mb-4">
          Amazon Advertising reports campaign efficiency using ACoS (Advertising Cost of Sales) — the
          percentage of attributed sales spent on ads — while most other platforms and finance teams speak in
          terms of ROAS. The two metrics are mathematical inverses of each other.
        </p>
        <p className="text-[15px] text-slate-600 leading-relaxed">
          The conversion formula is <span className="font-semibold text-slate-800">ROAS = 100 / ACoS%</span>.
          A 25% ACoS is therefore equivalent to a 4.00x ROAS, while a 50% ACoS equals a 2.00x ROAS. Lower ACoS
          always means higher ROAS, so use this converter to communicate Amazon campaign performance in the
          same language as your other ad channels.
        </p>
      </section>
    </div>
  )
}

export default AcosConverter
